import { lazy, Suspense } from 'react'
import RouteLoader from 'components/Loader/RouteLoader'

const CHUNK_LOAD_RETRY_KEY = 'lazy-route-chunk-reload-attempted'

const isChunkLoadError = (error) => {
  const message = `${error?.name || ''} ${error?.message || ''}`
  return /ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module/i.test(message)
}

const getHasRetriedChunkLoad = () => {
  try {
    return window.sessionStorage.getItem(CHUNK_LOAD_RETRY_KEY) === 'true'
  } catch (error) {
    return false
  }
}

const setHasRetriedChunkLoad = (value) => {
  try {
    if (value) {
      window.sessionStorage.setItem(CHUNK_LOAD_RETRY_KEY, 'true')
      return
    }

    window.sessionStorage.removeItem(CHUNK_LOAD_RETRY_KEY)
  } catch (error) {
    // Ignore storage write failures in restricted browser contexts.
  }
}

const loadRouteWithChunkRecovery = async (importFunction) => {
  try {
    const module = await importFunction()
    if (typeof window !== 'undefined') {
      setHasRetriedChunkLoad(false)
    }
    return module
  } catch (error) {
    if (typeof window !== 'undefined' && isChunkLoadError(error) && !getHasRetriedChunkLoad()) {
      setHasRetriedChunkLoad(true)
      window.location.reload()
      return new Promise(() => {})
    }

    if (typeof window !== 'undefined') {
      setHasRetriedChunkLoad(false)
    }

    throw error
  }
}

// Utility function to create lazy-loaded components with Suspense
export const createLazyRoute = (importFunction, loadingMessage = 'Loading...') => {
  const LazyComponent = lazy(() => loadRouteWithChunkRecovery(importFunction))

  return (props) => (
    <Suspense fallback={<RouteLoader message={loadingMessage} />}>
      <LazyComponent {...props} />
    </Suspense>
  )
}

// Route-specific loading messages
export const LOADING_MESSAGES = {
  AUTH: 'Loading authentication...',
  EMPLOYER: 'Loading employer dashboard...',
  CONTRACTOR: 'Loading job seeker dashboard...',
  PROFILE: 'Loading profile...',
  MESSAGES: 'Loading messages...',
  SETTINGS: 'Loading settings...',
  JOBS: 'Loading jobs...',
  CANDIDATES: 'Loading candidates...',
  PUBLIC: 'Loading page...',
  HELP: 'Loading help...',
  PRICING_PLANS: 'Loading pricing plans...',
  CHECKOUT_PLAN: 'Loading checkout plan...',
}
